import { fromGid } from '@/lib/shopify/gid';
import {
  getCompareAtPriceAmount,
  getElevarCurrencyCode,
  getMoneyAmount,
  getUserData,
  initElevarDataLayer,
  resolveElevarCategory,
} from '@/lib/elevar/shared';
import {
  ELEVAR_BRAND,
  ELEVAR_IMPRESSIONS_MAX_CHARS,
  ELEVAR_REFERRING_COLLECTION_KEY,
} from '@/lib/elevar/constants';
import { getVariantBasedOnLastSelectedOptions } from '@/utils/product/getLastSelectedVariant';

const resolveCardVariant = (product) =>
  getVariantBasedOnLastSelectedOptions(product) ?? product?.variants?.[0];

const buildImpression = (product, slug, position, pricing) => {
  const variant = resolveCardVariant(product);

  if (!variant) return null;

  const { sale, full, hasPriceDifference } = pricing.variant(variant);

  return {
    id: variant.sku || '',
    name: product.title || '',
    brand: ELEVAR_BRAND,
    category: resolveElevarCategory(product),
    price: getMoneyAmount(sale ?? variant?.price),
    compare_at_price: getCompareAtPriceAmount(hasPriceDifference, full, sale ?? variant?.price),
    list: `/collections/${slug}`,
    product_id: String(fromGid(product.id || '')),
    variant: variant.title || '',
    variant_id: String(fromGid(variant.id || '')),
    position,
  };
};

/**
 * Pushes `dl_view_item_list` for a collection grid.
 * Uses persisted size selection (default Queen) per card, matching collection card pricing.
 * Stops adding impressions when serialized payload would exceed `ELEVAR_IMPRESSIONS_MAX_CHARS`.
 */
export async function elevarViewItemList(cardProducts, slug, pricing) {
  if (!cardProducts?.length || !initElevarDataLayer()) return;

  const impressions = [];
  let charCount = 2; // accounts for the outer [ and ] of the serialized array
  let currencySource;

  for (let index = 0; index < cardProducts.length; index += 1) {
    const impression = buildImpression(
      cardProducts[index],
      slug,
      impressions.length + 1,
      pricing,
    );

    if (!impression) continue;

    currencySource ??= resolveCardVariant(cardProducts[index]);

    // Simulates JSON serialization to strictly enforce Elevar's < 4000 chars limit
    // Prevents payload from breaking without inaccurately slicing the array
    const chunk = JSON.stringify(impression);
    const delta = chunk.length + (impressions.length > 0 ? 1 : 0); // +1 for comma separator

    if (charCount + delta > ELEVAR_IMPRESSIONS_MAX_CHARS) break;

    impressions.push(impression);
    charCount += delta;
  }

  if (!impressions.length) return;

  // Persist referring list path for downstream PDP and AddToCart events
  sessionStorage.setItem(ELEVAR_REFERRING_COLLECTION_KEY, `/collections/${slug}`);

  window.ElevarDataLayer.push({
    event: 'dl_view_item_list',
    user_properties: getUserData(),
    ecommerce: {
      currencyCode: getElevarCurrencyCode(currencySource),
      impressions,
    },
  });
}
